import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
} from "@mui/material";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { useSearchParams } from "react-router-dom";
import firestoreInstance from "../../services/firestoreInstance";
import {
  fetchBoards,
  getBoards,
  getTournoi,
} from "../../features/tournoi/tournoiSlice";
import { useAppDispatch, useAppSelector } from "../../store";
import ResultatsHeader from "./ResultatsHeader";
import TableauSelector from "./TableauSelector";
import TableauShell, { type TableauNavHandlers } from "./TableauShell";
import {
  applyResultsNavPatch,
  parseResultsNav,
  resolveSelectedTableauId,
  type BracketDisplayMode,
  type ResultsView,
} from "./navigation";
import type { PlayerDirectory, ResultsDoc } from "./types";
import {
  directoryFromSpiddParticipants,
  extractSpiddBoardName,
  findBoardForSpiddLabel,
  identityFromPlayerDoc,
} from "./displayHelpers";
import styles from "./Resultats.module.css";

function mergePlayerDirectories(
  licences: string[],
  spidd: PlayerDirectory,
  firestore: PlayerDirectory
): PlayerDirectory {
  const out: PlayerDirectory = {};
  for (const licence of licences) {
    const fromSpidd = spidd[licence];
    const fromFs = firestore[licence];
    if (fromSpidd?.hasName) out[licence] = fromSpidd;
    else if (fromFs?.hasName) out[licence] = fromFs;
    else if (fromSpidd) out[licence] = fromSpidd;
    else if (fromFs) out[licence] = fromFs;
  }
  return out;
}

export default function Resultats(): React.ReactElement {
  const dispatch = useAppDispatch();
  const tournoi = useAppSelector(getTournoi);
  const boards = useAppSelector(getBoards);
  const [params, setParams] = useSearchParams();
  const filter = params.get("tableau");
  const nav = useMemo(() => parseResultsNav(params), [params]);

  const [docs, setDocs] = useState<ResultsDoc[]>([]);
  const [firestorePlayers, setFirestorePlayers] = useState<PlayerDirectory>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tournoi?.id) return;
    void dispatch(fetchBoards());
  }, [dispatch, tournoi?.id]);

  const patchNav = useCallback(
    (patch: Parameters<typeof applyResultsNavPatch>[1]) => {
      setParams(applyResultsNavPatch(params, patch), { replace: false });
    },
    [params, setParams]
  );

  const handlers: TableauNavHandlers = useMemo(
    () => ({
      setView: (view: ResultsView) => {
        if (view === "overview") {
          patchNav({
            view: "overview",
            poule: null,
            tour: null,
            mode: null,
          });
          return;
        }
        if (view === "pools") {
          patchNav({ view: "pools", tour: null, mode: null });
          return;
        }
        if (view === "bracket") {
          patchNav({ view: "bracket", poule: null });
          return;
        }
        patchNav({ view: "player", poule: null, tour: null });
      },
      setPoule: (pouleId: string | null) => {
        patchNav({
          view: "pools",
          poule: pouleId,
          tour: null,
          mode: null,
        });
      },
      setJoueur: (licence: string | null) => {
        patchNav({ joueur: licence });
      },
      setTour: (tourId: string | null) => {
        patchNav({
          view: "bracket",
          tour: tourId,
          poule: null,
        });
      },
      setMode: (mode: BracketDisplayMode) => {
        patchNav({
          view: "bracket",
          mode: mode === "rounds" ? null : mode,
          poule: null,
        });
      },
      openPlayer: (selection) => {
        patchNav({
          view: "player",
          joueur: selection.licence,
          tableau: selection.tableauId ?? undefined,
          poule: null,
          tour: null,
          mode: null,
        });
      },
    }),
    [patchNav]
  );

  useEffect(() => {
    if (!tournoi?.id) return;
    setLoading(true);
    const unsubscribe = onSnapshot(
      collection(
        firestoreInstance,
        "tournois",
        String(tournoi.id),
        "spiddResultats"
      ),
      (snapshot) => {
        setDocs(
          snapshot.docs.map((d) => ({
            ...(d.data() as Omit<ResultsDoc, "id">),
            id: d.id,
          }))
        );
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [tournoi?.id]);

  const sortedDocs = useMemo(() => {
    return [...docs].sort((a, b) => {
      const nameA =
        findBoardForSpiddLabel(boards, a.label)?.name ??
        extractSpiddBoardName(a.label) ??
        a.label;
      const nameB =
        findBoardForSpiddLabel(boards, b.label)?.name ??
        extractSpiddBoardName(b.label) ??
        b.label;
      return nameA.localeCompare(nameB, "fr", { numeric: true });
    });
  }, [docs, boards]);

  const preferredTableauIds = useMemo(() => {
    if (!nav.joueur || sortedDocs.length <= 1) return [];
    return sortedDocs
      .filter(
        (d) =>
          (d.participants ?? []).some((p) => p.licence === nav.joueur) ||
          d.pools.some((p) =>
            p.standings.some((s) => s.licence === nav.joueur)
          ) ||
          d.rounds.some((r) =>
            r.matches.some((m) => m.players.includes(nav.joueur))
          )
      )
      .map((d) => d.sourceTableauId);
  }, [sortedDocs, nav.joueur]);

  const selectedTableauId = useMemo(
    () =>
      resolveSelectedTableauId(
        sortedDocs,
        filter,
        preferredTableauIds.length === 1 ? preferredTableauIds : []
      ),
    [sortedDocs, filter, preferredTableauIds]
  );

  const activeDoc = useMemo(
    () =>
      sortedDocs.find((d) => d.sourceTableauId === selectedTableauId) ?? null,
    [sortedDocs, selectedTableauId]
  );

  const selectTableau = useCallback(
    (tableauId: string) => {
      patchNav({
        tableau: tableauId,
        poule: null,
        tour: null,
        mode: null,
      });
    },
    [patchNav]
  );

  useEffect(() => {
    if (!selectedTableauId || filter === selectedTableauId) return;
    setParams(
      applyResultsNavPatch(params, { tableau: selectedTableauId }),
      { replace: true }
    );
  }, [selectedTableauId, filter, params, setParams]);

  const spiddDirectory = useMemo(() => {
    const directory: PlayerDirectory = {};
    for (const doc of docs) {
      Object.assign(
        directory,
        directoryFromSpiddParticipants(doc.participants)
      );
    }
    return directory;
  }, [docs]);

  const licences = useMemo(
    () =>
      Array.from(
        new Set(
          docs
            .flatMap((d) => [
              ...d.pools.flatMap((p) => [
                ...p.standings.map((s) => s.licence),
                ...p.matches.flatMap((m) => m.players),
              ]),
              ...d.rounds.flatMap((r) =>
                r.matches.flatMap((m) => m.players)
              ),
              ...(d.participants ?? []).map((p) => p.licence),
            ])
            .filter((x): x is string => Boolean(x))
        )
      ).sort(),
    [docs]
  );

  const fallbackLicenceKey = useMemo(
    () =>
      licences
        .filter((licence) => !spiddDirectory[licence]?.hasName)
        .join("|"),
    [licences, spiddDirectory]
  );

  useEffect(() => {
    if (!fallbackLicenceKey) {
      setFirestorePlayers({});
      return;
    }
    let cancelled = false;

    async function loadFallbackPlayers(): Promise<void> {
      const ids = fallbackLicenceKey.split("|");
      const batches: string[][] = [];
      for (let i = 0; i < ids.length; i += 30) {
        batches.push(ids.slice(i, i + 30));
      }
      try {
        const results = await Promise.all(
          batches.map((batch) =>
            getDocs(
              query(
                collection(firestoreInstance, "players"),
                where("licenceId", "in", batch)
              )
            )
          )
        );
        if (cancelled) return;
        const found: PlayerDirectory = {};
        results.forEach((snap) => {
          snap.docs.forEach((docSnap) => {
            const value = docSnap.data() as Record<string, unknown>;
            if (value.licenceId == null) return;
            const licenceId = String(value.licenceId);
            found[licenceId] = identityFromPlayerDoc(licenceId, value);
          });
        });
        setFirestorePlayers(found);
      } catch {
        if (!cancelled) setFirestorePlayers({});
      }
    }

    void loadFallbackPlayers();
    return () => {
      cancelled = true;
    };
  }, [fallbackLicenceKey]);

  const players = useMemo(
    () => mergePlayerDirectories(licences, spiddDirectory, firestorePlayers),
    [licences, spiddDirectory, firestorePlayers]
  );

  return (
    <Container maxWidth="xl" className={styles.page}>
      <ResultatsHeader
        subtitle="Tirage des poules, classements au fil des scores, puis tableau final."
      />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress aria-label="Chargement des résultats" />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les résultats : {error}
        </Alert>
      )}

      {!loading && !error && sortedDocs.length === 0 && (
        <Alert severity="info">
          Aucun résultat disponible pour le moment.
        </Alert>
      )}

      {!loading && !error && selectedTableauId && (
        <TableauSelector
          docs={sortedDocs}
          boards={boards}
          value={selectedTableauId}
          onChange={selectTableau}
        />
      )}

      {!loading && !error && activeDoc && (
        <TableauShell
          key={activeDoc.id}
          doc={activeDoc}
          boards={boards}
          players={players}
          searchDocs={sortedDocs}
          tournoiLabel={tournoi?.label}
          nav={nav}
          handlers={handlers}
        />
      )}
    </Container>
  );
}
